// Fuehrt den Versandkostenfrei-Hinweis der PDP-Kaufzeile nach.
//
// Den Ausgangswert rechnet blocks/_mk-buy-row.liquid — `cart` steht im SSR zur
// Verfuegung, der Hinweis stimmt also beim Seitenaufbau ohne dieses Modul.
// Nachgefuehrt werden die drei Groessen, die sich danach ohne Reload aendern:
// MENGE, WARENKORB und VARIANTE.
//
// Die Variante gehoert MIT, obwohl der Variantenwechsel die Section neu
// rendert: eingehaengt wird davon nur der Picker, alles andere aktualisieren die
// Komponenten selbst — und der Hinweis ist ein GESCHWISTER von <product-price>,
// nicht sein Kind. Ohne diesen Listener zeigt die Kaufzeile 34,80 € und rechnet
// mit 8,95 € weiter.
import { ThemeEvents } from '@theme/events';

const HINT = '[data-mk-shipping-hint]';
// Lookup ueber das Attribut, Zustand ueber die Klasse — dieselbe Trennung, die
// mk-variant-required.js beim Setzen einhaelt.
const PRICE = '[data-mk-price]';
const CHOICE_OPEN = 'mk-choice--open';
const LABEL = '[data-mk-ship-label]';
const FREE = 'mk-shipping-free';

class MkShippingHint extends HTMLElement {
  connectedCallback() {
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCart);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariant);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCart);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariant);
  }

  // Der Preis der neuen Variante steht im Event; ihn aus dem gerenderten Text zu
  // lesen scheitert an der Formatierung ("34,80 €") und an der zweiten,
  // versteckten Preisfassung des Auswahlzwangs.
  #onVariant = (event) => {
    const price = Number(event.detail?.resource?.price);
    if (!Number.isFinite(price)) return;
    const hint = this.querySelector(HINT);
    if (hint) hint.dataset.mkShipUnit = String(price);
    // Erst im naechsten Frame: mk-variant-required.js haengt am SELBEN Event und
    // nimmt dort die Klasse mk-choice--open weg. Wer zuerst laeuft, entscheidet
    // die Registrierungs-, also die Ladereihenfolge — synchron gerendert waere
    // der Zustand eine Wette, und danach folgt kein Ereignis mehr, das ihn
    // korrigiert.
    requestAnimationFrame(() => this.#render());
  };

  #onQuantity = (event) => {
    const quantity = Number(event.detail?.quantity);
    if (Number.isFinite(quantity) && quantity > 0) this.#render(quantity);
  };

  // Nach einem Warenkorb-Update ist der SSR-Wert veraltet. Der Gesamtbetrag steckt
  // nicht zuverlaessig im Event, deshalb wird er am Anker fortgeschrieben.
  #onCart = (event) => {
    const total = Number(event.detail?.resource?.total_price);
    if (!Number.isFinite(total)) return;
    const hint = this.querySelector(HINT);
    if (hint) hint.dataset.mkShipCart = String(total);
    this.#render();
  };

  #render(quantity) {
    const hint = this.querySelector(HINT);
    const label = hint?.querySelector(LABEL);
    if (!hint || !label) return;

    // Nicht versandpflichtig (Kurs, Druck-Gutschein) — dann gibt es keinen
    // Versandteil, an dem etwas nachzufuehren waere.
    if (hint.hasAttribute('data-mk-ship-irrelevant')) return;
    const threshold = Number(hint.dataset.mkShipThreshold);
    const cart = Number(hint.dataset.mkShipCart);
    // Bei offener Wahl zeigt der Preis daneben "Ab <price_min>" — derselbe Betrag
    // gilt hier. Shopify loest schon aus einer TEILwahl eine volle Variante auf und
    // raet den Rest; ihr Preis stuende sonst neben einem Von-Preis.
    // Im EIGENEN Element gesucht: die Seite fuehrt mehrere Preisbloecke
    // (Kaufzeile, Sticky-Leiste, Vorschlagssuche), und nur der hier umschlossene
    // gehoert zu diesem Hinweis.
    const price = this.querySelector(PRICE);
    const open = price?.classList.contains(CHOICE_OPEN) ?? false;
    const unit = Number(open ? hint.dataset.mkShipUnitMin : hint.dataset.mkShipUnit);
    // Ohne hinterlegte Schwelle gibt es keine Aussage — dann nichts anfassen.
    if (!(threshold > 0) || !Number.isFinite(cart) || !Number.isFinite(unit)) return;

    const count = quantity ?? this.#quantity();
    const free = cart + unit * count >= threshold;
    const text = free ? label.dataset.mkShipTextFree : label.dataset.mkShipTextExtra;
    if (text && label.textContent !== text) label.textContent = text;
    label.classList.toggle(FREE, free);
  }

  #quantity() {
    const field = this.querySelector('quantity-selector-component input[ref="quantityInput"]');
    const value = Number(field?.value);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }
}

if (!customElements.get('mk-shipping-hint')) {
  customElements.define('mk-shipping-hint', MkShippingHint);
}
